import objectMapImage from './images/Object Map.png';
import automationsMapImage from './images/Automations Map.png';
import workflowRulesImage from './images/Workflow Rules Migration Plan.png';
import securityRisksImage from './images/Security Risks.png';
import fieldCleanupImage from './images/Field Cleanup.png';
import cpqTemplateImage from './images/CPQ Template.png';
import apexAnalysisImage from './images/APEX analysis.png';
import processOptimizationImage from './images/Process Optimization.png';

export interface ArtifactTemplate {
  id: string;
  title: string;
  subtitle: string;
  prompt: string;
  image?: string;
}

export const artifactTemplates: ArtifactTemplate[] = [
  {
    id: 'object-map',
    title: 'Object Map',
    subtitle: 'Visualize your top objects and their components.',
    image: objectMapImage,
    prompt: `Create an artifact of my top 100 Salesforce objects ranked by field count.

For each object, show:
- Total field count
- Breakdown of component types (custom fields, standard fields, formula fields, relationships)
- Automation count (flows, process builders, workflow rules, triggers)
- Validation rule count

Include a visual chart showing object comparison and filters to drill down by component type.

Present summary metrics at the top showing total objects analyzed, total fields across all objects, and most complex object.`,
  },
  {
    id: 'automations-map',
    title: 'Automations Map',
    subtitle: 'See all automation across your org at a glance.',
    image: automationsMapImage,
    prompt: `Analyze all automation in my Salesforce org and Create an artifact showing counts and breakdowns for:
- Flows (active vs inactive)
- Process Builders (active vs inactive)
- Workflow Rules (active vs inactive)
- Apex Triggers

Group automation by object and include a distribution chart showing automation types.

Add filters to view by automation type, status (active/inactive), and object.

Display summary metrics: total automation count, active vs inactive ratio, most automated object, and legacy automation count (Workflow Rules + Process Builders that should be migrated).`,
  },
  {
    id: 'workflow-rules-migration',
    title: 'Workflow Rules Migration Plan',
    subtitle: 'Plan your migration from Workflow Rules to Flows.',
    image: workflowRulesImage,
    prompt: `Create an artifact for Workflow Rules migration plan for my org.

Identify all objects with Workflow Rules, ranked by rule count.

For each object, list all Workflow Rules with:
- Rule name
- Evaluation criteria
- Actions performed (email alerts, field updates, tasks, outbound messages)
- Complexity score (simple/medium/complex)

Group rules by migration priority (high/medium/low) based on complexity and business impact.

Include a migration roadmap with:
- Total rules to migrate
- Estimated effort per group
- Recommended migration order
- Potential consolidation opportunities

Add visual charts showing rule distribution by object and complexity breakdown.`,
  },
  {
    id: 'security-risks',
    title: 'Security Risks',
    subtitle: 'Identify and fix security vulnerabilities.',
    image: securityRisksImage,
    prompt: `Scan my Salesforce org for security risks and Create an artifact for a security audit.

Analyze:
- Profiles with excessive permissions (ModifyAllData, ViewAllData, ViewEncryptedData)
- Sharing rules that may expose sensitive data
- Permission sets with admin-level access
- Objects without field-level security
- Users with admin privileges who haven't logged in recently
- Publicly accessible sites or communities

Categorize each risk by severity (critical/high/medium/low) and provide specific remediation steps for each finding.

Include summary metrics:
- Total risks found
- Risk distribution by severity
- Highest-risk profiles
- Compliance gaps

Add filters to view by risk type, severity, and affected objects.`,
  },
  {
    id: 'field-cleanup',
    title: 'Field Cleanup',
    subtitle: 'Find unused and duplicate fields to clean up.',
    image: fieldCleanupImage,
    prompt: `Analyze custom fields across my Salesforce org and produce an artifact for cleanup analysis.

Create two rankings:
- The top 5 objects by total custom field count
- The top 5 objects by number of custom fields with 0% utilization

For each object appearing in either ranking, include:
- The total custom field count
- The count of 0% utilization fields
- Utilization percentage (percent of records with non-null values)

Then, for each object, flag fields for cleanup consideration using these categories:
- Unused fields (0% utilization for 90+ days)
- Duplicate fields (similar names or overlapping purpose)
- Deprecated fields (marked obsolete in description/help text)
- Stale fields (not modified in 2+ years and low utilization)

Categorize flagged fields by cleanup priority (high/medium/low) based on utilization and age.

Include summary metrics across the org:
- Total fields analyzed
- Total cleanup candidates
- Potential storage savings
- The objects with the highest cleanup opportunity

Add interactive filters to explore results by object, utilization threshold, field age (creation date / last modified date), cleanup category (unused/duplicate/deprecated/stale), and ranking type (field count vs. 0%-utilization count).`,
  },
  {
    id: 'cpq-template',
    title: 'CPQ Template',
    subtitle: 'Audit and optimize Salesforce CPQ configuration.',
    image: cpqTemplateImage,
    prompt: `Analyze all CPQ configuration in my Salesforce org and create an artifact showing:

Configuration Inventory:
- Counts and breakdowns: Price Rules, Product Rules, Configuration Attributes, Quote/Contract/Subscription Line Fields (active vs inactive)
- Group by object (Product2, Pricebook2, SBQQ__Quote, SBQQ__QuoteLine, SBQQ__Contract, SBQQ__ContractLine, etc.)
- Distribution chart of CPQ component types and status

Configuration Relationships:
- Map relationships between Price Rules, Product Rules, and Configuration Attributes
- Dependencies between CPQ objects and standard/custom objects
- Fields referenced by CPQ configurations

CPQ Settings & Analysis:
- CPQ package settings and feature enablement
- CPQ-specific validation rules, automation, and approval processes

Summary Metrics:
- Total CPQ configuration count, active vs inactive ratio, most configured CPQ object
- Configuration complexity score (rules and relationships)
- Missing/incomplete configurations (e.g., products without price rules)

Filters: By component type, status (active/inactive), object, complexity

Recommendations: Redundant/conflicting rules, inactive configs to review, rule evaluation order optimizations, missing configs impacting quote accuracy`,
  },
  {
    id: 'apex-analysis',
    title: 'APEX analysis',
    subtitle: 'Analyze, refactor, and de-risk Salesforce Apex code.',
    image: apexAnalysisImage,
    prompt: `Analyze all Apex code in my Salesforce org and create an artifact showing:

Apex Inventory:
- Apex Classes: total count, active vs inactive, test vs non-test classes, character count and LOC per class
- Apex Triggers: total count, active vs inactive, grouped by object, character count and LOC per trigger

Error Analysis:
- All classes/triggers with compilation errors (syntax errors, missing dependencies)
- Error details: type, message, affected component, grouped by severity (blocking vs warnings)

Code Quality Metrics:
- Average character count per class/trigger, top 10 largest by character count
- Classes/triggers exceeding Salesforce limits (1MB per class/trigger)
- Test coverage percentage per class, classes without test classes

Dependency & Usage Analysis:
- Dependencies between Apex classes, circular dependencies
- Apex classes referenced by Flows, Process Builders, or other automations
- Apex classes referencing specific Salesforce objects
- Unused Apex classes (not referenced by triggers, flows, or other classes)
- Objects with most Apex triggers, Apex classes per object

Summary Metrics:
- Total classes/triggers count, total character count, error count (by severity)
- Average test coverage percentage, most automated object (by trigger count)

Filters: By component type (Class/Trigger), status (active/inactive, with/without errors), object, character count range, test coverage percentage

Recommendations: Fix compilation errors, refactor oversized classes, consolidate redundant code, create test classes for uncovered code, flag deprecated/unused code for removal`,
  },
  {
    id: 'process-optimization',
    title: 'Process Optimization',
    subtitle: 'Analyze and optimize automation and sales processes.',
    image: processOptimizationImage,
    prompt: `Analyze automation and configuration for Opportunity, Case, and Lead objects and create an artifact showing:

Automation Inventory:
- Total counts: Flows, Process Builders, Workflow Rules, Apex Triggers, Validation Rules (active vs inactive) per object
- Legacy automation count (Workflow Rules + Process Builders to migrate to Flows)

Process Analysis:
- Automation triggering on stage/status changes (Opportunity stages, Case statuses, Lead statuses)
- Approval processes (Opportunity), Escalation Rules (Case), Assignment Rules (Lead)

Summary Metrics:
- Total automation count per object, active vs inactive ratio
- Legacy automation count per object

Recommendations: Migrate Workflow Rules/Process Builders to Flows, consolidate redundant automation, optimize automation triggering on all changes

Output format: Recommendations should be the most accessible section at the top of the artifact`,
  },
];
